/**
 * LIGHT STYLES (2026-10-07, Matt: "I want the types to represent generic fixture types, for
 * example wall sconce, pendant, chandelier, step light, etc. And then when those are placed,
 * they all have a consistent symbol for their type. Then, within the instance parameters of
 * the fixture, i want to be able to choose a set 'style' and have the 3D representation
 * changes"). The library was surveyed against Forest Studio's built work (57 projects): the
 * gooseneck barn light, the coach / gas lantern and the plain box lantern lead, with ship's
 * cage lights, pier-top lanterns, iron chandeliers and the usual interior pendants behind.
 *
 * A TYPE is the generic fixture (Sconce, Pendant, Chandelier, Flush Mount, Step Light, Post /
 * Pier Lantern, Path Light) and owns the plan symbol. The STYLE is an INSTANCE choice
 * (one param per kind — `sconceStyle`, `pendantStyle` … — so each kind's list is its own) and
 * owns the 3D body only. This file is the catalog every consumer reads: the 3D builder
 * (bim-geometry fixture/lightStyles3d.ts) draws to these sizes, the pick box and z extent come
 * from them, and the walkthrough light sits at each style's own `bulb`.
 *
 * FINISH: every style has a DEFAULT finish (`finish` — a hex for a coated / painted look, or a
 * bare metal token `metal:<key>` — BARE_METALS in colors.ts). The Paint Brush recolors the
 * fixture's metal parts only (`paintColor` on the instance, resolved like any other surface
 * over the shared Light Fixture finish material below); glass, shades, candles and bulbs keep
 * their own look.
 *
 * Units: INCHES, in the kind's local frame —
 *   wall kinds (sconce, stepLight): x along the wall centred on 0, y off the wall face
 *     (y = 0 = the face), z about the MOUNT point (the box centre — the mount height);
 *   ceiling kinds (pendant, chandelier, flushMount): x/y centred on the anchor, z = 0 the
 *     ceiling, the fixture hanging down; a hanging style's `drop` is ceiling → bottom;
 *   ground kinds (postLantern, pathLight): x/y centred on the anchor, z = 0 the base (the
 *     ground, or a pier top via the instance's Base Height).
 */

export type LightBody = 'metal' | 'plaster'

export interface LightStyle {
  value: string
  label: string
  /** the default finish token — '#rrggbb' (coated / painted) or 'metal:<bare metal key>' */
  finish: string
  /** which finish material the brushable body is (what colors fit it) */
  body?: LightBody
  /** footprint across (x) and its y extent, inches. Wall kinds: y0 = 0 (the face) → y1.
   *  Ceiling / ground kinds: centred, so y0 = −d/2 and y1 = d/2. */
  w: number
  y0: number
  y1: number
  /** wall + ground kinds: the body's z extent (inches, see the frame note above).
   *  Ceiling kinds: the BODY height measured up from its bottom (the part below the rod /
   *  chain), which rides at the bottom of the drop. */
  z0: number
  z1: number
  /** hanging ceiling styles: the default overall drop, ceiling → bottom (inches) */
  drop?: number
  /** the lamp centre (inches). Ceiling kinds: z measured UP FROM THE BOTTOM of the fixture. */
  bulb: [number, number, number]
}

const BLACK = '#1e1e1e'
const IRON = '#26231f'

// ---- wall sconces (mount point = the box centre) ----
const SCONCE: LightStyle[] = [
  { value: 'barnDome', label: 'Gooseneck Barn Light — Dome', finish: BLACK, w: 14.4, y0: 0, y1: 24.3, z0: -4.0, z1: 8.2, bulb: [0, 17, -1.2] },
  { value: 'barnCone', label: 'Gooseneck Barn Light — Cone', finish: BLACK, w: 13.4, y0: 0, y1: 23.8, z0: -4.5, z1: 8.2, bulb: [0, 17, -1.8] },
  { value: 'coach', label: 'Coach Lantern', finish: BLACK, w: 9.4, y0: 0, y1: 11.5, z0: -10.5, z1: 14.8, bulb: [0, 6.6, -1.2] },
  { value: 'gas', label: 'Gas Lantern (Tall)', finish: IRON, w: 10.6, y0: 0, y1: 13.6, z0: -14.6, z1: 17.8, bulb: [0, 8.3, -3.5] },
  { value: 'box', label: 'Box Lantern', finish: BLACK, w: 7.8, y0: 0, y1: 7.6, z0: -7.1, z1: 7.8, bulb: [0, 4.4, 0] },
  { value: 'bulkhead', label: "Ship's Cage Light", finish: 'metal:bronze', w: 12.4, y0: 0, y1: 13.2, z0: -8.3, z1: 4.4, bulb: [0, 7, -3.3] },
  { value: 'library', label: 'Swing-Arm Library Sconce', finish: 'metal:agedBrass', w: 9.8, y0: 0, y1: 16.0, z0: -2.6, z1: 11.3, bulb: [0, 11, 5.6] },
  { value: 'halfBowl', label: 'Plaster Half Bowl', finish: '#e9e3d6', body: 'plaster', w: 11.0, y0: 0, y1: 5.6, z0: -5.3, z1: 4.4, bulb: [0, 2.6, 1.5] },
  // CASEWORK STUDIO stage 4 (Matt: "art lights"): the House of Troy / Visual Comfort class —
  // a round backplate, a swept arm, a long hood washing the art below it. Mount = the plate.
  { value: 'picture', label: 'Picture Light (Art)', finish: 'metal:agedBrass', w: 18.7, y0: 0, y1: 8.4, z0: -1.6, z1: 6.2, bulb: [0, 7.0, 3.6] },
]

// ---- step lights (mount point = the faceplate centre) ----
const STEP: LightStyle[] = [
  { value: 'louver', label: 'Louvered Rectangle', finish: 'metal:bronze', w: 6.6, y0: 0, y1: 1.3, z0: -1.6, z1: 1.6, bulb: [0, 0.6, -0.4] },
  { value: 'eyelid', label: 'Round Eyelid', finish: 'metal:bronze', w: 4.4, y0: 0, y1: 1.6, z0: -2.2, z1: 2.2, bulb: [0, 0.6, -0.5] },
]

// ---- pendants (z0..z1 = the body, measured up from its bottom) ----
const PENDANT: LightStyle[] = [
  { value: 'warehouse', label: 'Industrial Dome', finish: BLACK, w: 16.6, y0: -8.3, y1: 8.3, z0: 0, z1: 9.6, drop: 30, bulb: [0, 0, 2.4] },
  { value: 'lantern', label: 'Hanging Coach Lantern', finish: BLACK, w: 13.4, y0: -6.7, y1: 6.7, z0: 0, z1: 31.0, drop: 44, bulb: [0, 0, 12.5] },
  { value: 'octagon', label: 'Octagonal Lantern', finish: IRON, w: 18.1, y0: -9.05, y1: 9.05, z0: 0, z1: 31.5, drop: 44, bulb: [0, 0, 12] },
  { value: 'cube', label: 'Open Cage Cube', finish: BLACK, w: 18.4, y0: -9.2, y1: 9.2, z0: 0, z1: 18.4, drop: 36, bulb: [0, 0, 8] },
  { value: 'cage', label: 'Cage Pendant', finish: 'metal:bronze', w: 12.4, y0: -6.2, y1: 6.2, z0: 0, z1: 13.4, drop: 30, bulb: [0, 0, 5.0] },
  { value: 'schoolhouse', label: 'Schoolhouse Globe', finish: BLACK, w: 10.4, y0: -5.2, y1: 5.2, z0: 0, z1: 13.0, drop: 30, bulb: [0, 0, 5.5] },
  { value: 'cloche', label: 'Glass Bell (Cloche)', finish: 'metal:agedBrass', w: 9.4, y0: -4.7, y1: 4.7, z0: 0, z1: 12.5, drop: 34, bulb: [0, 0, 4.5] },
  { value: 'drum', label: 'Fabric Drum', finish: 'metal:bronze', w: 20.4, y0: -10.2, y1: 10.2, z0: 0, z1: 11.0, drop: 30, bulb: [0, 0, 5] },
  { value: 'cone', label: 'Metal Cone', finish: BLACK, w: 12.4, y0: -6.2, y1: 6.2, z0: 0, z1: 13.0, drop: 30, bulb: [0, 0, 2.2] },
]

// ---- chandeliers ----
const CHANDELIER: LightStyle[] = [
  { value: 'candleArm', label: 'Candle Arm (French Country)', finish: IRON, w: 29.4, y0: -13.0, y1: 13.0, z0: 0, z1: 22.0, drop: 36, bulb: [0, 0, 10] },
  { value: 'candleArm2', label: 'Candle Arm — Two Tier', finish: IRON, w: 33.4, y0: -16.7, y1: 16.7, z0: 0, z1: 30.0, drop: 42, bulb: [0, 0, 13] },
  { value: 'ring', label: 'Iron Ring (Spanish)', finish: IRON, w: 32.0, y0: -16.0, y1: 16.0, z0: 0, z1: 19.0, drop: 36, bulb: [0, 0, 4] },
  { value: 'ring2', label: 'Iron Ring — Two Tier', finish: IRON, w: 36.0, y0: -17.0, y1: 17.0, z0: 0, z1: 27.0, drop: 42, bulb: [0, 0, 8] },
]

// ---- flush and semi-flush mounts (fixed height — z0..z1 = the body, up from its bottom) ----
const FLUSH: LightStyle[] = [
  { value: 'drum', label: 'Fabric Drum', finish: 'metal:nickel', w: 16.4, y0: -8.2, y1: 8.2, z0: 0, z1: 7.0, drop: 7.0, bulb: [0, 0, 3] },
  { value: 'cage', label: "Ship's Cage", finish: 'metal:bronze', w: 9.4, y0: -4.7, y1: 4.7, z0: 0, z1: 9.5, drop: 9.5, bulb: [0, 0, 4] },
  { value: 'schoolhouse', label: 'Schoolhouse (Semi-Flush)', finish: BLACK, w: 10.4, y0: -5.2, y1: 5.2, z0: 0, z1: 12.0, drop: 12.0, bulb: [0, 0, 5] },
]

// ---- post and pier lanterns (z = 0 the base) ----
const POST: LightStyle[] = [
  { value: 'pierHip', label: 'Pier-Top Lantern (Hip Roof)', finish: 'metal:bronze', w: 12.0, y0: -6.0, y1: 6.0, z0: 0, z1: 22.6, bulb: [0, 0, 8] },
  { value: 'pierPagoda', label: 'Pier-Top Lantern (Pagoda Roof)', finish: 'metal:bronze', w: 13.44, y0: -6.72, y1: 6.72, z0: 0, z1: 24.25, bulb: [0, 0, 8] },
  { value: 'postArm', label: 'Post with Hanging Lantern', finish: BLACK, w: 11.6, y0: -3.0, y1: 33.8, z0: 0, z1: 99.6, bulb: [0, 26, 68] },
]

// ---- path lights (z = 0 the ground) ----
const PATH: LightStyle[] = [
  { value: 'mushroom', label: 'Mushroom Path Light', finish: 'metal:bronze', w: 9.2, y0: -4.6, y1: 4.6, z0: 0, z1: 21.6, bulb: [0, 0, 19.2] },
  { value: 'bollard', label: 'Louvered Bollard', finish: BLACK, w: 6.4, y0: -3.2, y1: 3.2, z0: 0, z1: 30.6, bulb: [0, 0, 26] },
]

/** each styled kind's own list (first = the default) and the instance param that holds it */
export const LIGHT_STYLES: Record<string, { param: string; styles: LightStyle[] }> = {
  sconce: { param: 'sconceStyle', styles: SCONCE },
  stepLight: { param: 'stepStyle', styles: STEP },
  pendant: { param: 'pendantStyle', styles: PENDANT },
  chandelier: { param: 'chandelierStyle', styles: CHANDELIER },
  flushMount: { param: 'flushStyle', styles: FLUSH },
  postLantern: { param: 'postStyle', styles: POST },
  pathLight: { param: 'pathStyle', styles: PATH },
}

export const STYLED_LIGHT_KINDS: readonly string[] = Object.keys(LIGHT_STYLES)
/** hanging ceiling kinds — their overall drop is an instance value */
export const HANGING_LIGHT_KINDS: readonly string[] = ['pendant', 'chandelier']

export function isStyledLightKind(kind: unknown): boolean {
  return typeof kind === 'string' && kind in LIGHT_STYLES
}

/**
 * The style a fixture is built in — its kind's instance style param, else the kind's first
 * (default) style. `params` = the merged instance + type params (a ResolvedElement's
 * `{ ...typeParams, ...params }`, or either alone — the style lives on the instance, the kind
 * on the type). null for a kind with no styles.
 */
export function lightStyleOf(kind: unknown, params: Record<string, unknown> | undefined): LightStyle | null {
  const k = typeof kind === 'string' ? LIGHT_STYLES[kind] : undefined
  if (!k) return null
  const v = params?.[k.param]
  return k.styles.find((s) => s.value === v) ?? k.styles[0]
}

/** the overall drop of a hanging style in FEET — the instance's own, else the style's */
export function lightDropFt(style: LightStyle, params: Record<string, unknown> | undefined): number {
  const v = params?.dropFt
  const body = (style.z1 - style.z0) / 12
  const own = typeof v === 'number' && isFinite(v) && v > 0 ? v : (style.drop ?? style.z1) / 12
  return Math.max(own, body)
}

/**
 * The body's z span in FEET in the kind's frame — what the pick box, the section cut and the
 * 3D element's extent all read (fixtureZSpan composes it with the mount height / base height).
 * Wall + ground kinds: the catalog's own span. Ceiling kinds: [−drop, 0].
 */
export function lightStyleZSpanFt(kind: string, style: LightStyle, params: Record<string, unknown> | undefined): { z0: number; z1: number } {
  if (kind === 'pendant' || kind === 'chandelier') return { z0: -lightDropFt(style, params), z1: 0 }
  if (kind === 'flushMount') return { z0: -(style.z1 - style.z0) / 12, z1: 0 }
  return { z0: style.z0 / 12, z1: style.z1 / 12 }
}

/** the bulb centre in FEET in the kind's frame (ceiling kinds: z below the ceiling) */
export function lightBulbFt(kind: string, style: LightStyle, params: Record<string, unknown> | undefined): [number, number, number] {
  const [x, y, z] = style.bulb
  if (kind === 'pendant' || kind === 'chandelier') return [x / 12, y / 12, -lightDropFt(style, params) + z / 12]
  if (kind === 'flushMount') return [x / 12, y / 12, -(style.z1 - style.z0) / 12 + z / 12]
  return [x / 12, y / 12, z / 12]
}

/** the style's choice list for a schema param */
export function lightStyleChoices(kind: string): { value: string; label: string }[] {
  return (LIGHT_STYLES[kind]?.styles ?? []).map((s) => ({ value: s.value, label: s.label }))
}

// ---- the finish materials the brushable body is made of ----
//
// One shared material per body kind, so the project can set a STANDARD color for every light
// fixture at once (Color Standards) and the brush's "what fits" list is right: a metal body
// takes bare metals and factory / RAL coatings (the sheet-metal rule), a plaster body takes
// paint and integral color.
export const LIGHT_BODY_MATERIALS: Record<LightBody, Record<string, unknown>> = {
  metal: {
    id: 'lightFixtureMetal01', firmId: 'lightFixtureMetal01', name: 'Light Fixture Metal', color: '#2a2826',
    category: 'Metal', substrate: 'sheetMetal', surface: { color: '#2a2826' },
    cut: { pattern: 'solid', fgColor: '#5a5e61', bgColor: '#e4e2de' }, render: { metalness: 0.35, roughness: 0.5 },
  },
  plaster: {
    id: 'lightFixturePlaster01', firmId: 'lightFixturePlaster01', name: 'Light Fixture Plaster', color: '#e9e3d6',
    category: 'Finish', substrate: 'stucco', surface: { color: '#e9e3d6' },
    cut: { pattern: 'solid', fgColor: '#8a8478', bgColor: '#f1ede4' }, render: { metalness: 0, roughness: 0.9 },
  },
}
export const lightBodyMaterialId = (body: LightBody | undefined): string =>
  String(LIGHT_BODY_MATERIALS[body || 'metal'].id)
